<template>
  <div class="peer-support-settings">
    <!-- Header with Toggle -->
    <div class="availability-header">
      <b-form-checkbox
        v-model="isAvailable"
        switch
        @change="onAvailabilityChange"
      ></b-form-checkbox>
      <div class="schedule-label">
        <i class="fas fa-calendar-alt mr-2"></i>
        <span>Schedule</span>
        <span v-if="!isAvailable && enabledDaysCount > 0" class="badge badge-muted ml-2">
          Paused
        </span>
        <span v-else-if="enabledDaysCount > 0" class="badge badge-gold ml-2">
          {{ enabledDaysCount }} day{{ enabledDaysCount > 1 ? 's' : '' }}
        </span>
        <i
          v-if="enabledDaysCount > 0 || isAvailable"
          class="fas ml-2 schedule-arrow"
          :class="isScheduleExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          @click="isScheduleExpanded = !isScheduleExpanded"
        ></i>
      </div>
    </div>

    <!-- Schedule Panel (visible when expanded, regardless of toggle) -->
    <transition name="slide-fade">
      <div v-if="isScheduleExpanded" class="schedule-panel" :class="{ 'schedule-panel-disabled': !isAvailable }">

        <!-- Disabled overlay message -->
        <div v-if="!isAvailable && enabledDaysCount > 0" class="disabled-notice">
          <i class="fas fa-eye-slash mr-2"></i>
          <span>Schedule paused — members cannot see your availability</span>
        </div>

        <!-- Day Selector -->
        <div class="day-selector" :class="{ 'disabled-section': !isAvailable }">
          <div class="day-selector-label">
            <i class="fas fa-hand-pointer mr-1"></i> Select days
          </div>
          <div class="days-row">
            <div
              v-for="day in days"
              :key="day.key"
              class="day-chip"
              :class="{
                'day-chip-active': localSchedule[day.key].enabled && isAvailable,
                'day-chip-paused': localSchedule[day.key].enabled && !isAvailable
              }"
              @click="isAvailable && toggleDay(day.key)"
            >
              {{ day.abbr }}
            </div>
          </div>
        </div>

        <!-- Time Settings for Enabled Days -->
        <div v-if="enabledDays.length > 0" class="time-settings" :class="{ 'disabled-section': !isAvailable }">
          <transition-group name="slide">
            <div
              v-for="day in enabledDays"
              :key="day.key"
              class="time-row"
              :class="{ 'time-row-disabled': !isAvailable }"
            >
              <div class="time-row-day">
                <span class="time-row-dot" :class="{ 'dot-paused': !isAvailable }"></span>
                <span class="time-row-name">{{ day.label }}</span>
              </div>
              <div class="time-row-inputs">
                <time-picker
                  :value="localSchedule[day.key].start"
                  :disabled="!isAvailable"
                  @input="val => updateTime(day.key, 'start', val)"
                />
                <span class="time-dash">&ndash;</span>
                <time-picker
                  :value="localSchedule[day.key].end"
                  :disabled="!isAvailable"
                  @input="val => updateTime(day.key, 'end', val)"
                />
              </div>
              <button
                v-if="isAvailable"
                class="time-row-remove"
                @click="toggleDay(day.key)"
                title="Remove day"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </transition-group>
        </div>

        <!-- Empty State (only when available and no days) -->
        <div v-else-if="isAvailable" class="empty-state">
          <i class="fas fa-clock"></i>
          <span>Select days above to set your availability hours</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import TimePicker from "./TimePicker.vue";

export default {
  name: "PeerSupportSettings",
  components: { TimePicker },
  props: {
    schedule: {
      type: [Object, String],
      default: null,
    },
  },
  data() {
    return {
      isAvailable: false,
      isScheduleExpanded: false,
      localSchedule: this.getDefaultSchedule(),
      days: [
        { key: "monday", label: "Monday", abbr: "M" },
        { key: "tuesday", label: "Tuesday", abbr: "T" },
        { key: "wednesday", label: "Wednesday", abbr: "W" },
        { key: "thursday", label: "Thursday", abbr: "T" },
        { key: "friday", label: "Friday", abbr: "F" },
        { key: "saturday", label: "Saturday", abbr: "S" },
        { key: "sunday", label: "Sunday", abbr: "S" },
      ],
    };
  },
  computed: {
    enabledDays() {
      return this.days.filter(day => this.localSchedule[day.key].enabled);
    },
    enabledDaysCount() {
      return this.enabledDays.length;
    },
  },
  watch: {
    schedule: {
      handler(val) {
        this.parseSchedule(val);
      },
      immediate: true,
    },
  },
  methods: {
    getDefaultSchedule() {
      return {
        monday: { enabled: false, start: "09:00", end: "17:00" },
        tuesday: { enabled: false, start: "09:00", end: "17:00" },
        wednesday: { enabled: false, start: "09:00", end: "17:00" },
        thursday: { enabled: false, start: "09:00", end: "17:00" },
        friday: { enabled: false, start: "09:00", end: "17:00" },
        saturday: { enabled: false, start: "09:00", end: "17:00" },
        sunday: { enabled: false, start: "09:00", end: "17:00" },
      };
    },
    parseSchedule(val) {
      if (!val) {
        this.localSchedule = this.getDefaultSchedule();
        this.isAvailable = false;
        this.isScheduleExpanded = false;
        return;
      }

      let parsed = val;
      if (typeof val === "string") {
        try {
          parsed = JSON.parse(val);
        } catch (e) {
          this.localSchedule = this.getDefaultSchedule();
          this.isAvailable = false;
          this.isScheduleExpanded = false;
          return;
        }
      }

      if (typeof parsed === "object" && parsed !== null) {
        const defaultSchedule = this.getDefaultSchedule();
        let hasAnyEnabled = false;

        Object.keys(defaultSchedule).forEach((day) => {
          if (parsed[day]) {
            this.localSchedule[day] = {
              enabled: parsed[day].enabled || false,
              start: this.clampTime(parsed[day].start || "09:00"),
              end: this.clampTime(parsed[day].end || "17:00"),
            };
            if (parsed[day].enabled) {
              hasAnyEnabled = true;
            }
          } else {
            this.localSchedule[day] = defaultSchedule[day];
          }
        });

        // If data has explicit available flag, use it; otherwise infer from days
        if (typeof parsed.available === 'boolean') {
          this.isAvailable = parsed.available;
        } else {
          this.isAvailable = hasAnyEnabled;
        }
      }
    },
    onAvailabilityChange(value) {
      if (!value) {
        // When turning off, keep schedule intact but collapse
        this.isScheduleExpanded = false;
      } else {
        // When turning on, expand the schedule
        this.isScheduleExpanded = true;
      }
      this.emitChange();
    },
    toggleDay(dayKey) {
      const isEnabling = !this.localSchedule[dayKey].enabled;
      this.localSchedule[dayKey].enabled = isEnabling;

      // When enabling a new day, copy times from an existing enabled day
      if (isEnabling && this.enabledDays.length > 1) {
        const existingDay = this.enabledDays.find(d => d.key !== dayKey);
        if (existingDay) {
          this.localSchedule[dayKey].start = this.localSchedule[existingDay.key].start;
          this.localSchedule[dayKey].end = this.localSchedule[existingDay.key].end;
        }
      }

      this.emitChange();
    },
    updateTime(dayKey, type, value) {
      // Ensure the value stays within valid range (01:00 – 23:59, no midnight)
      this.localSchedule[dayKey][type] = this.clampTime(value);
      this.emitChange();
    },

    clampTime(timeStr) {
      if (!timeStr) return "09:00";
      const parts = String(timeStr).split(":");
      let h = parseInt(parts[0], 10);
      const m = parseInt(parts[1] || "0", 10);
      if (isNaN(h) || h < 0) h = 9;
      if (h > 23) h = 23;
      const clampedM = isNaN(m) ? 0 : Math.min(59, Math.max(0, m));
      return `${String(h).padStart(2, "0")}:${String(clampedM).padStart(2, "0")}`;
    },
    emitChange() {
      const data = {
        available: this.isAvailable,
        ...this.localSchedule,
      };
      this.$emit("update:schedule", JSON.stringify(data));
      this.$emit("change", {
        schedule: data,
      });
    },
  },
};
</script>

<style scoped>
.peer-support-settings {
  padding: 0;
}

/* Header */
.availability-header {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.schedule-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #333;
  margin-left: 15px;
}

.schedule-arrow {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.schedule-arrow:hover {
  color: #d6b674;
  background-color: rgba(214, 182, 116, 0.15);
}

.badge-gold {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: white;
  font-size: 11px;
  font-weight: 500;
  border-radius: 12px;
  padding: 3px 10px;
}

.badge-muted {
  background-color: #aaa;
  color: white;
  font-size: 11px;
  font-weight: 500;
  border-radius: 12px;
  padding: 3px 10px;
}

/* Schedule Panel */
.schedule-panel {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.08) 0%, rgba(214, 182, 116, 0.03) 100%);
  border: 1px solid rgba(214, 182, 116, 0.25);
  border-radius: 10px;
  padding: 18px;
  margin-top: 4px;
  transition: all 0.3s ease;
}

.schedule-panel-disabled {
  background: linear-gradient(135deg, rgba(150, 150, 150, 0.06) 0%, rgba(150, 150, 150, 0.02) 100%);
  border-color: rgba(150, 150, 150, 0.2);
}

/* Disabled notice */
.disabled-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  margin-bottom: 14px;
  background-color: rgba(108, 117, 125, 0.08);
  border: 1px solid rgba(108, 117, 125, 0.15);
  border-radius: 8px;
  color: #888;
  font-size: 0.8rem;
  font-weight: 500;
}

.disabled-notice i {
  color: #aaa;
}

/* Disabled section overlay */
.disabled-section {
  opacity: 0.5;
  pointer-events: none;
}

/* Day Selector */
.day-selector {
  margin-bottom: 16px;
  transition: opacity 0.3s ease;
}

.day-selector-label {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 10px;
  font-weight: 500;
}

.days-row {
  display: flex;
  gap: 8px;
}

.day-chip {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s ease;
  user-select: none;
  background: white;
  border: 2px solid #e0e0e0;
  color: #888;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.day-chip:hover {
  border-color: #d6b674;
  color: #d6b674;
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.2);
  transform: translateY(-1px);
}

.day-chip-active {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 3px 10px rgba(214, 182, 116, 0.35);
}

.day-chip-active:hover {
  color: white;
  box-shadow: 0 3px 12px rgba(214, 182, 116, 0.45);
  opacity: 0.9;
}

/* Paused day chip — greyed out version of active */
.day-chip-paused {
  background: #bbb;
  border-color: transparent;
  color: white;
  box-shadow: none;
  cursor: default;
}

.day-chip-paused:hover {
  border-color: transparent;
  color: white;
  box-shadow: none;
  transform: none;
}

/* Time Settings */
.time-settings {
  border-top: 1px solid rgba(214, 182, 116, 0.2);
  padding-top: 14px;
  transition: opacity 0.3s ease;
}

.time-row {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.time-row:hover {
  border-color: rgba(214, 182, 116, 0.4);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.time-row:last-child {
  margin-bottom: 0;
}

.time-row-disabled {
  background: #f5f5f5;
  border-color: #e8e8e8;
  box-shadow: none;
}

.time-row-disabled:hover {
  border-color: #e8e8e8;
  box-shadow: none;
}

.time-row-day {
  display: flex;
  align-items: center;
  min-width: 110px;
}

.time-row-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  margin-right: 10px;
  flex-shrink: 0;
}

.dot-paused {
  background: #bbb;
}

.time-row-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #444;
}

.time-row-disabled .time-row-name {
  color: #999;
}

.time-row-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.time-dash {
  color: #bbb;
  font-weight: 500;
  font-size: 0.9rem;
}

.time-row-remove {
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-left: 8px;
  font-size: 0.75rem;
}

.time-row-remove:hover {
  background-color: rgba(220, 53, 69, 0.08);
  color: #dc3545;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 15px;
  border-top: 1px solid rgba(214, 182, 116, 0.2);
  margin-top: 0;
  color: #aaa;
  font-size: 0.82rem;
}

.empty-state i {
  font-size: 1rem;
  color: #d6b674;
  opacity: 0.5;
}

/* Slide Fade Transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Slide Transition for time rows */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Responsive */
@media (max-width: 576px) {
  .schedule-panel {
    padding: 14px;
  }

  .days-row {
    gap: 6px;
  }

  .day-chip {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }

  .time-row {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
  }

  .time-row-day {
    min-width: auto;
    flex: 1;
  }

  .time-row-inputs {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .time-row-remove {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  .time-row {
    position: relative;
  }

  .disabled-notice {
    font-size: 0.72rem;
    padding: 8px 10px;
  }
}

@media (max-width: 350px) {
  .schedule-panel {
    padding: 10px 8px;
  }

  .availability-header {
    padding: 8px 0;
  }

  .schedule-label {
    margin-left: 8px;
    font-size: 0.82rem;
    flex-wrap: wrap;
    gap: 4px;
  }

  .days-row {
    gap: 4px;
    flex-wrap: wrap;
  }

  .day-chip {
    width: 30px;
    height: 30px;
    font-size: 11px;
  }

  .day-selector-label {
    font-size: 0.7rem;
  }

  .time-row {
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 8px;
  }

  .time-row-day {
    font-size: 0.78rem;
  }

  .time-row-inputs {
    flex: 1 1 100%;
    justify-content: flex-start;
    gap: 4px;
  }

  .time-row-remove {
    position: absolute;
    right: 6px;
    top: 8px;
  }

  .time-row {
    position: relative;
  }

  .disabled-notice {
    font-size: 0.68rem;
    padding: 6px 8px;
    text-align: center;
  }

  .empty-state {
    font-size: 0.78rem;
    padding: 12px 8px;
  }

  .badge-gold,
  .badge-muted {
    font-size: 10px;
    padding: 2px 7px;
  }
}
</style>
